import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { objectFitContainRect } from './utils/objectFitContainRect';
import ExploreUnknownMinigameMapMarker from './exploreUnknownMinigames/ExploreUnknownMinigameMapMarker';

/** Must match `exploreUnknownPortalLayout.js` POI authoring (logical design canvas). */
const DEFAULT_AUTHORING_WIDTH = 1920;
const DEFAULT_AUTHORING_HEIGHT = 1080;

/**
 * Convert pointer client coordinates to authoring canvas px (1920×1080 design space).
 */
function clientToAuthoringPoint(
  svgEl,
  clientX,
  clientY,
  {
    useNaturalAlignment,
    logicalWidth,
    logicalHeight,
    naturalWidth,
    naturalHeight,
    authoringWidth,
    authoringHeight,
  }
) {
  if (!svgEl) {
    throw new Error('ExploreUnknownArrivalPointsOverlay: SVG element is required for coordinate conversion');
  }
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) {
    throw new Error('ExploreUnknownArrivalPointsOverlay: getScreenCTM() returned null');
  }
  const p = pt.matrixTransform(ctm.inverse());
  const nx = p.x;
  const ny = p.y;

  if (useNaturalAlignment) {
    const clampedX = Math.max(0, Math.min(naturalWidth, nx));
    const clampedY = Math.max(0, Math.min(naturalHeight, ny));
    return [
      (clampedX / naturalWidth) * authoringWidth,
      (clampedY / naturalHeight) * authoringHeight,
    ];
  }

  const clampedX = Math.max(0, Math.min(logicalWidth, nx));
  const clampedY = Math.max(0, Math.min(logicalHeight, ny));
  return [
    (clampedX / logicalWidth) * authoringWidth,
    (clampedY / logicalHeight) * authoringHeight,
  ];
}

/**
 * Points of interest on shadowverse arrival: path + per-mission nodes.
 * States: available (clickable), done, unavailable.
 *
 * When `naturalWidth` + `naturalHeight` are set (from the loaded map `<img>`), the overlay
 * matches CSS `object-fit: contain`: same sub-rect as the bitmap, `viewBox` in image pixels.
 */
function ExploreUnknownArrivalPointsOverlay({
  logicalWidth,
  logicalHeight,
  points,
  pointStates,
  onAvailableMissionClick,
  /** Global Explore Unknown mission index for points[0] (1 = first mission on arrival map, 6 = first on cave). */
  firstMissionIndex = 1,
  zIndex = 13100,
  pathClassName = '',
  /** POI coordinates in layout file are authored vs this canvas (default 1920×1080). */
  authoringWidth = DEFAULT_AUTHORING_WIDTH,
  authoringHeight = DEFAULT_AUTHORING_HEIGHT,
  /** From loaded image `naturalWidth` / `naturalHeight`; when set, POIs align with `object-fit: contain`. */
  naturalWidth,
  naturalHeight,
  /** Development: drag handles to tune positions (authoring px reported upstream). */
  waypointEditMode = false,
  onWaypointAuthoringChange,
  /** Optional POI: Rite of respite (authoring px). Placed beside the unlocking mission. */
  darknessBargainAuthoringPoint = null,
  darknessBargainVisible = false,
  /** Insert into the path after this mission node index (0-based): … → mission[i] → rite → mission[i+1] → … */
  darknessBargainPolylineInsertAfterIndex = null,
  darknessBargainInteractive = false,
  onDarknessBargainClick,
  /** Optional map minigame marker (authoring px, random per run). */
  minigameAuthoringPoint = null,
  minigameVisible = false,
  minigameInteractive = false,
  onMinigameClick,
  /** Global mission indices to render as boss waypoints (red warning triangle + DANGER label). */
  bossMissionIndices = null,
}) {
  const svgRef = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);

  if (
    typeof authoringWidth !== 'number' ||
    typeof authoringHeight !== 'number' ||
    authoringWidth <= 0 ||
    authoringHeight <= 0
  ) {
    throw new Error('ExploreUnknownArrivalPointsOverlay: authoringWidth/authoringHeight must be positive numbers');
  }

  if (
    typeof firstMissionIndex !== 'number' ||
    !Number.isInteger(firstMissionIndex) ||
    firstMissionIndex < 1
  ) {
    throw new Error(`ExploreUnknownArrivalPointsOverlay: invalid firstMissionIndex (${firstMissionIndex})`);
  }

  if (waypointEditMode && typeof onWaypointAuthoringChange !== 'function') {
    throw new Error(
      'ExploreUnknownArrivalPointsOverlay: onWaypointAuthoringChange is required when waypointEditMode is true'
    );
  }

  const useNaturalAlignment =
    typeof naturalWidth === 'number' &&
    typeof naturalHeight === 'number' &&
    naturalWidth > 0 &&
    naturalHeight > 0;

  const { svgPoints, svgStyle, viewBox, preserveAspectRatio } = useMemo(() => {
    if (!Array.isArray(points) || points.length === 0) {
      return {
        svgPoints: [],
        svgStyle: {
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          zIndex,
          pointerEvents: 'none',
        },
        viewBox: `0 0 ${logicalWidth} ${logicalHeight}`,
        preserveAspectRatio: 'none',
      };
    }
    if (useNaturalAlignment) {
      const rect = objectFitContainRect(logicalWidth, logicalHeight, naturalWidth, naturalHeight);
      const mapped = points.map(([px, py]) => [
        (px / authoringWidth) * naturalWidth,
        (py / authoringHeight) * naturalHeight,
      ]);
      return {
        svgPoints: mapped,
        svgStyle: {
          position: 'absolute',
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
          zIndex,
          pointerEvents: waypointEditMode ? 'auto' : 'none',
        },
        viewBox: `0 0 ${naturalWidth} ${naturalHeight}`,
        preserveAspectRatio: 'xMidYMid meet',
      };
    }
    return {
      svgPoints: points,
      svgStyle: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex,
        pointerEvents: waypointEditMode ? 'auto' : 'none',
      },
      viewBox: `0 0 ${logicalWidth} ${logicalHeight}`,
      preserveAspectRatio: 'none',
    };
  }, [
    useNaturalAlignment,
    logicalWidth,
    logicalHeight,
    naturalWidth,
    naturalHeight,
    authoringWidth,
    authoringHeight,
    points,
    zIndex,
    waypointEditMode,
  ]);

  const darknessBargainSvgPoint = useMemo(() => {
    if (!darknessBargainVisible || !Array.isArray(darknessBargainAuthoringPoint) || darknessBargainAuthoringPoint.length !== 2) {
      return null;
    }
    const [bx, by] = darknessBargainAuthoringPoint;
    if (typeof bx !== 'number' || typeof by !== 'number' || Number.isNaN(bx) || Number.isNaN(by)) {
      throw new Error('ExploreUnknownArrivalPointsOverlay: darknessBargainAuthoringPoint must be numeric [x, y]');
    }
    if (useNaturalAlignment) {
      return [
        (bx / authoringWidth) * naturalWidth,
        (by / authoringHeight) * naturalHeight,
      ];
    }
    return [bx, by];
  }, [
    darknessBargainVisible,
    darknessBargainAuthoringPoint,
    useNaturalAlignment,
    authoringWidth,
    authoringHeight,
    naturalWidth,
    naturalHeight,
  ]);

  const minigameSvgPoint = useMemo(() => {
    if (!minigameVisible || !Array.isArray(minigameAuthoringPoint) || minigameAuthoringPoint.length !== 2) {
      return null;
    }
    const [mx, my] = minigameAuthoringPoint;
    if (typeof mx !== 'number' || typeof my !== 'number' || Number.isNaN(mx) || Number.isNaN(my)) {
      throw new Error('ExploreUnknownArrivalPointsOverlay: minigameAuthoringPoint must be numeric [x, y]');
    }
    if (useNaturalAlignment) {
      return [
        (mx / authoringWidth) * naturalWidth,
        (my / authoringHeight) * naturalHeight,
      ];
    }
    return [mx, my];
  }, [
    minigameVisible,
    minigameAuthoringPoint,
    useNaturalAlignment,
    authoringWidth,
    authoringHeight,
    naturalWidth,
    naturalHeight,
  ]);

  const polylineSvgPoints = useMemo(() => {
    if (
      !darknessBargainVisible ||
      darknessBargainSvgPoint == null ||
      darknessBargainPolylineInsertAfterIndex == null ||
      !Number.isInteger(darknessBargainPolylineInsertAfterIndex)
    ) {
      return svgPoints;
    }
    const i = darknessBargainPolylineInsertAfterIndex;
    if (i < 0 || i >= svgPoints.length) {
      throw new Error(
        `ExploreUnknownArrivalPointsOverlay: darknessBargainPolylineInsertAfterIndex out of range (${i} vs ${svgPoints.length})`
      );
    }
    return [...svgPoints.slice(0, i + 1), darknessBargainSvgPoint, ...svgPoints.slice(i + 1)];
  }, [
    svgPoints,
    darknessBargainVisible,
    darknessBargainSvgPoint,
    darknessBargainPolylineInsertAfterIndex,
  ]);

  const emitAuthoringFromClient = useCallback(
    (clientX, clientY, index) => {
      const [ax, ay] = clientToAuthoringPoint(svgRef.current, clientX, clientY, {
        useNaturalAlignment,
        logicalWidth,
        logicalHeight,
        naturalWidth: naturalWidth ?? 0,
        naturalHeight: naturalHeight ?? 0,
        authoringWidth,
        authoringHeight,
      });
      onWaypointAuthoringChange(index, ax, ay);
    },
    [
      useNaturalAlignment,
      logicalWidth,
      logicalHeight,
      naturalWidth,
      naturalHeight,
      authoringWidth,
      authoringHeight,
      onWaypointAuthoringChange,
    ]
  );

  useEffect(() => {
    if (!waypointEditMode || draggingIndex === null) return undefined;

    const onMove = (e) => {
      emitAuthoringFromClient(e.clientX, e.clientY, draggingIndex);
    };
    const onUp = () => {
      setDraggingIndex(null);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [waypointEditMode, draggingIndex, emitAuthoringFromClient]);

  const bossMissionIndexSet = useMemo(() => {
    if (bossMissionIndices == null) return null;
    if (!Array.isArray(bossMissionIndices)) {
      throw new Error('ExploreUnknownArrivalPointsOverlay: bossMissionIndices must be an array');
    }
    const set = new Set();
    for (const idx of bossMissionIndices) {
      if (!Number.isInteger(idx) || idx < 1) {
        throw new Error(
          `ExploreUnknownArrivalPointsOverlay: bossMissionIndices entries must be positive integers (got ${idx})`
        );
      }
      set.add(idx);
    }
    return set;
  }, [bossMissionIndices]);

  if (!Array.isArray(points) || points.length === 0) {
    return null;
  }

  if (!Array.isArray(pointStates) || pointStates.length !== points.length) {
    throw new Error(
      `ExploreUnknownArrivalPointsOverlay: pointStates must match points length (got ${pointStates?.length} vs ${points.length})`
    );
  }

  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (!Array.isArray(p) || p.length !== 2) {
      throw new Error(
        `ExploreUnknownArrivalPointsOverlay: point ${i} must be [x, y], got ${JSON.stringify(p)}`
      );
    }
    const [x, y] = p;
    if (typeof x !== 'number' || typeof y !== 'number' || Number.isNaN(x) || Number.isNaN(y)) {
      throw new Error(`ExploreUnknownArrivalPointsOverlay: point ${i} must be numeric [x, y]`);
    }
    const st = pointStates[i];
    if (st !== 'available' && st !== 'done' && st !== 'unavailable') {
      throw new Error(`ExploreUnknownArrivalPointsOverlay: invalid state "${st}" at index ${i}`);
    }
  }

  const polylinePoints = polylineSvgPoints.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg
      ref={svgRef}
      className={`explore-unknown-arrival-poi-overlay ${pathClassName}`.trim()}
      style={svgStyle}
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      aria-hidden
    >
      <polyline
        points={polylinePoints}
        fill="none"
        stroke="rgba(0, 255, 200, 0.45)"
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={waypointEditMode ? { pointerEvents: 'none' } : undefined}
      />
      {darknessBargainSvgPoint ? (
        <g
          className="explore-unknown-darkness-bargain-poi"
          style={{
            pointerEvents: waypointEditMode || !darknessBargainInteractive ? 'none' : 'auto',
            cursor: darknessBargainInteractive && !waypointEditMode ? 'pointer' : 'default',
          }}
          role={darknessBargainInteractive ? 'button' : undefined}
          tabIndex={darknessBargainInteractive && !waypointEditMode ? 0 : undefined}
          onClick={
            waypointEditMode || !darknessBargainInteractive || typeof onDarknessBargainClick !== 'function'
              ? undefined
              : (e) => {
                  onDarknessBargainClick();
                  e.currentTarget.blur();
                }
          }
          onKeyDown={
            waypointEditMode || !darknessBargainInteractive || typeof onDarknessBargainClick !== 'function'
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onDarknessBargainClick();
                  }
                }
          }
        >
          <circle cx={darknessBargainSvgPoint[0]} cy={darknessBargainSvgPoint[1]} r="32" fill="transparent" />
          <circle
            cx={darknessBargainSvgPoint[0]}
            cy={darknessBargainSvgPoint[1]}
            r="14"
            fill={darknessBargainInteractive ? 'rgba(40, 20, 60, 0.92)' : 'rgba(28, 28, 36, 0.88)'}
            stroke={darknessBargainInteractive ? 'rgba(196, 181, 253, 0.95)' : 'rgba(100, 100, 120, 0.7)'}
            strokeWidth="3"
          />
          <text
            x={darknessBargainSvgPoint[0]}
            y={darknessBargainSvgPoint[1] + 5}
            textAnchor="middle"
            fill="rgba(230, 220, 255, 0.95)"
            fontSize="14"
            fontWeight="700"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            ☽
          </text>
        </g>
      ) : null}
      {minigameSvgPoint ? (
        <ExploreUnknownMinigameMapMarker
          x={minigameSvgPoint[0]}
          y={minigameSvgPoint[1]}
          interactive={minigameInteractive}
          onClick={onMinigameClick}
          waypointEditMode={waypointEditMode}
        />
      ) : null}
      {svgPoints.map(([x, y], i) => {
        const state = pointStates[i];
        const missionIndex = firstMissionIndex + i;
        const isBoss = bossMissionIndexSet != null && bossMissionIndexSet.has(missionIndex);
        if (isBoss) {
          return (
            <BossMissionWaypoint
              key={i}
              x={x}
              y={y}
              state={state}
              missionIndex={missionIndex}
              waypointEditMode={waypointEditMode}
              onAvailableMissionClick={onAvailableMissionClick}
            />
          );
        }
        if (state === 'available') {
          return (
            <g
              key={i}
              className="explore-unknown-poi-available-group"
              style={{
                pointerEvents: waypointEditMode ? 'none' : 'auto',
                cursor: waypointEditMode ? 'default' : 'pointer',
              }}
              role="button"
              tabIndex={waypointEditMode ? -1 : 0}
              onClick={
                waypointEditMode
                  ? undefined
                  : (e) => {
                      onAvailableMissionClick(missionIndex);
                      e.currentTarget.blur();
                    }
              }
              onKeyDown={
                waypointEditMode
                  ? undefined
                  : (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onAvailableMissionClick(missionIndex);
                      }
                    }
              }
            >
              <circle cx={x} cy={y} r="28" fill="transparent" />
              <circle
                className="explore-unknown-poi explore-unknown-poi--available explore-unknown-poi--available-glow"
                cx={x}
                cy={y}
                r="12"
                fill="none"
                stroke="rgba(0, 255, 200, 0.55)"
                strokeWidth="2"
                aria-hidden
              />
              <circle
                className="explore-unknown-poi explore-unknown-poi--available explore-unknown-poi--available-visual"
                cx={x}
                cy={y}
                r="12"
                fill="rgba(8, 24, 22, 0.92)"
                stroke="rgba(0, 255, 200, 1)"
                strokeWidth="3"
              />
            </g>
          );
        }
        if (state === 'done') {
          return (
            <g key={i} className="explore-unknown-poi explore-unknown-poi--done" aria-hidden>
              <circle
                cx={x}
                cy={y}
                r="14"
                fill="rgba(10, 36, 22, 0.92)"
                stroke="rgba(74, 222, 128, 0.9)"
                strokeWidth="2"
              />
              <path
                className="explore-unknown-poi-tick"
                d={`M ${x - 7.5} ${y + 0.5} L ${x - 2.5} ${y + 6} L ${x + 9.5} ${y - 8}`}
                fill="none"
                stroke="#4ade80"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        }
        return (
          <circle
            key={i}
            className={`explore-unknown-poi explore-unknown-poi--${state}`}
            cx={x}
            cy={y}
            r="10"
            fill="rgba(22, 22, 28, 0.9)"
            stroke="rgba(90, 90, 100, 0.95)"
            strokeWidth="2"
          />
        );
      })}
      {waypointEditMode
        ? svgPoints.map(([x, y], i) => (
            <circle
              key={`dev-handle-${i}`}
              className="explore-unknown-poi-dev-handle"
              cx={x}
              cy={y}
              r="24"
              fill="rgba(255, 60, 255, 0.2)"
              stroke="rgba(255, 255, 255, 0.95)"
              strokeWidth="2"
              style={{
                cursor: draggingIndex === i ? 'grabbing' : 'grab',
                pointerEvents: 'auto',
                touchAction: 'none',
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
                  e.currentTarget.setPointerCapture(e.pointerId);
                }
                setDraggingIndex(i);
                emitAuthoringFromClient(e.clientX, e.clientY, i);
              }}
            />
          ))
        : null}
    </svg>
  );
}

/**
 * Boss waypoint marker: red warning triangle with a black "DANGER" label on a yellow plate above it.
 * Replaces the regular circle for missions listed in `bossMissionIndices` (e.g. mission 10, the cave boss).
 *
 * Geometry (authoring px around centre `(x, y)`):
 *  - Triangle: equilateral pointing up, vertices at (x, y - 14), (x - 22, y + 24), (x + 22, y + 24).
 *  - Yellow "DANGER" plate: rounded rect centred at (x, y - 25), 56×14 px.
 *
 * State semantics mirror the regular circle marker:
 *  - `available`    – clickable, full red/yellow, hover/focus scale + glow ring.
 *  - `done`         – red/yellow at reduced opacity, with green tick overlay.
 *  - `unavailable`  – greyed out, not clickable.
 */
function BossMissionWaypoint({
  x,
  y,
  state,
  missionIndex,
  waypointEditMode,
  onAvailableMissionClick,
}) {
  const { t } = useTranslation();
  const dangerLabel = t('exploreUnknownPage.bossWaypointDangerLabel');
  const ariaLabel = t('exploreUnknownPage.bossWaypointAriaLabel', { missionIndex });

  const isAvailable = state === 'available';
  const isDone = state === 'done';
  const isUnavailable = state === 'unavailable';

  const triangleFill = isUnavailable ? '#3a3a3f' : '#dc2626';
  const triangleStroke = isUnavailable ? 'rgba(180, 180, 190, 0.9)' : '#000';
  const exclamationFill = isUnavailable ? 'rgba(220, 220, 230, 0.85)' : '#fff';
  const plateFill = isUnavailable ? '#5a5a60' : '#facc15';
  const plateStroke = isUnavailable ? 'rgba(180, 180, 190, 0.9)' : '#000';
  const plateText = isUnavailable ? 'rgba(230, 230, 235, 0.95)' : '#000';
  const groupOpacity = isDone ? 0.6 : 1;

  const trianglePoints = `${x},${y - 14} ${x - 22},${y + 24} ${x + 22},${y + 24}`;

  const groupStyle = {
    pointerEvents: waypointEditMode || !isAvailable ? 'none' : 'auto',
    cursor: !waypointEditMode && isAvailable ? 'pointer' : 'default',
    opacity: groupOpacity,
  };

  const handleClick =
    waypointEditMode || !isAvailable
      ? undefined
      : (e) => {
          onAvailableMissionClick(missionIndex);
          e.currentTarget.blur();
        };

  const handleKeyDown =
    waypointEditMode || !isAvailable
      ? undefined
      : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAvailableMissionClick(missionIndex);
          }
        };

  return (
    <g
      className={`explore-unknown-poi-boss-group explore-unknown-poi-boss-group--${state}`}
      role={isAvailable ? 'button' : undefined}
      tabIndex={isAvailable && !waypointEditMode ? 0 : -1}
      style={groupStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={isAvailable ? ariaLabel : undefined}
    >
      {/* Generous transparent click target spanning triangle + plate. */}
      <rect x={x - 30} y={y - 34} width="60" height="62" fill="transparent" />

      {/* Glow halo behind the boss marker (visible on hover/focus for available). */}
      {isAvailable ? (
        <polygon
          className="explore-unknown-poi-boss-glow"
          points={trianglePoints}
          fill="none"
          stroke="rgba(255, 80, 80, 0.65)"
          strokeWidth="3"
          strokeLinejoin="round"
          aria-hidden
        />
      ) : null}

      {/* Red warning triangle with black border. */}
      <polygon
        className="explore-unknown-poi-boss-triangle"
        points={trianglePoints}
        fill={triangleFill}
        stroke={triangleStroke}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* Exclamation mark inside the triangle. */}
      <text
        x={x}
        y={y + 16}
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="22"
        fontWeight="900"
        fill={exclamationFill}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        !
      </text>

      {/* Yellow "DANGER" plate above the triangle. */}
      <rect
        className="explore-unknown-poi-boss-plate"
        x={x - 28}
        y={y - 32}
        width="56"
        height="14"
        rx="2.5"
        ry="2.5"
        fill={plateFill}
        stroke={plateStroke}
        strokeWidth="1.25"
      />
      <text
        x={x}
        y={y - 21}
        textAnchor="middle"
        fontFamily="Arial Black, Arial, sans-serif"
        fontSize="10"
        fontWeight="900"
        letterSpacing="0.6"
        fill={plateText}
        textLength="48"
        lengthAdjust="spacingAndGlyphs"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {dangerLabel}
      </text>

      {/* Done tick overlay sits in the lower-right of the triangle. */}
      {isDone ? (
        <g aria-hidden>
          <circle
            cx={x + 14}
            cy={y + 18}
            r="8"
            fill="rgba(10, 36, 22, 0.95)"
            stroke="rgba(74, 222, 128, 0.95)"
            strokeWidth="1.5"
          />
          <path
            d={`M ${x + 10} ${y + 18} L ${x + 13} ${y + 21.5} L ${x + 18} ${y + 14.5}`}
            fill="none"
            stroke="#4ade80"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ) : null}
    </g>
  );
}

export default ExploreUnknownArrivalPointsOverlay;
